import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLoaderData } from "react-router-dom";

import { DeckGL } from "@deck.gl/react";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { Color, COORDINATE_SYSTEM, OrthographicView, OrthographicViewState } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { GeoJsonLayer } from "@deck.gl/layers";

import { aerialImageTileSpec } from "./api/Url";
import { Button } from "./components/Button";
import { Download, Upload } from "react-bootstrap-icons";
import { downloadCsv } from "./Csv";
import { ImageAlignLoaderData } from "./loaders/imageAlignLoader";
import { LanczosBitmapLayer } from "./components/LanczosBitmapLayer";
import { loadPairs, savePairs, NUDGE, RectStatus, makeRectPolygon, RECT_SIZE, RectPair, MemoPairRow, PairRow, PairSrc, filledLineColor, MAX_DEV, MEAN_DX, MEAN_DY, selectedLineColor } from "./ImageAlignmentPageUtil";
import { List } from "react-window";

export function ImageAlignPage() {
    const [activeOpacity,   setActiveOpacity]   = useState<number>( 1.0 );
    const [inactiveOpacity, setInactiveOpacity] = useState<number>( 0.0 );
    const [hideB, setBTransparent] = useState<boolean>( false );
    const { imageA, imageB } = useLoaderData() as ImageAlignLoaderData;
    const [offset, setOffset] = useState<[number, number]>( [0, 0] );
    const [hideTiles, setHideTiles] = useState( false );
    const [showPairs, setShowPairs] = useState( true  );

    const loaded = useMemo( () => loadPairs( imageA.aerialImageId, imageB.aerialImageId ), [imageA, imageB] );
    const [pairs, setPairs] = useState<RectPair[]>( loaded.pairs );
    const [nextId, setNextId] = useState( loaded.nextId );

    // --- multi-select state ---
    const [selectedIds, setSelectedIds] = useState<Set<number>>( new Set() );

    // --- box selection state ---
    const [boxStart, setBoxStart] = useState<[number, number] | null>( null );
    const [boxEnd,   setBoxEnd]   = useState<[number, number] | null>( null );
    const [isBoxSelecting, setIsBoxSelecting] = useState( false );
    const containerRef = useRef<HTMLDivElement>( null );

    const bWidth  = Math.ceil( imageB.width / 512.0 ) * 512;
    const bHeight = Math.ceil( imageB.height / 512.0 ) * 512;
    const scaleX  = imageB.pixelWidth / imageA.pixelWidth;
    const scaleY  = imageB.pixelHeight / imageA.pixelHeight;

    const [viewState, setViewState] = useState<OrthographicViewState>( () => ( {
        target: [Math.round( imageB.width / 2 ), Math.round( imageB.height / 2 ), 0],
        zoom: 3
    } ) );

    // persist on change
    useEffect( () => {
        savePairs( imageA.aerialImageId, imageB.aerialImageId, pairs );
    }, [pairs, imageA, imageB] );

    // derive a "primary" selected pair for the info panel (first in selection)
    const primarySelectedId = useMemo( () => {
        if( selectedIds.size === 0 ) return undefined;
        return selectedIds.values().next().value;
    }, [selectedIds] );

    // center view on a pair
    const panToSelected = useCallback( ( pair: RectPair ) => {
        setViewState( vs => ( { ...vs, target: [pair.bx, pair.by, 0] } ) );
    }, [] );

    const removePair = useCallback( ( id: number ) => {
        setPairs( prev => prev.filter( p => p.id !== id ) );
        setSelectedIds( prev => {
            const next = new Set( prev );
            next.delete( id );
            return next;
        } );
    }, [] );

    const selectPair = useCallback( ( pair: RectPair ) => {
        setSelectedIds( new Set( [pair.id] ) );
        panToSelected( pair );
    }, [panToSelected] );

    // --- screen-to-world coordinate conversion ---
    const screenToWorld = useCallback( ( sx: number, sy: number ): [number, number] => {
        const el = containerRef.current;
        if( !el ) return [0, 0];
        const { width, height } = el.getBoundingClientRect();
        const zoom = ( viewState as any ).zoom ?? 0;
        const target = ( viewState as any ).target ?? [0, 0, 0];
        const scale = Math.pow( 2, zoom );
        const wx = ( sx - width / 2 ) / scale + target[0];
        const wy = ( sy - height / 2 ) / scale + target[1];
        return [wx, wy];
    }, [viewState] );

    // --- box selection: capture-phase mousedown to intercept before DeckGL ---
    useEffect( () => {
        const el = containerRef.current;
        if( !el ) return;

        const onDown = ( e: PointerEvent ) => {
            if( e.shiftKey && e.button === 0 ) {
                e.stopPropagation();
                e.preventDefault();
                const rect = el.getBoundingClientRect();
                const sx = e.clientX - rect.left;
                const sy = e.clientY - rect.top;
                setBoxStart( [sx, sy] );
                setBoxEnd( [sx, sy] );
                setIsBoxSelecting( true );
            }
        };

        el.addEventListener( "pointerdown", onDown, true ); // capture phase
        return () => el.removeEventListener( "pointerdown", onDown, true );
    }, [] );

    const handleBoxMove = useCallback( ( e: React.PointerEvent ) => {
        if( !isBoxSelecting ) return;
        const rect = containerRef.current!.getBoundingClientRect();
        setBoxEnd( [e.clientX - rect.left, e.clientY - rect.top] );
    }, [isBoxSelecting] );

    const handleBoxEnd = useCallback( ( e: React.PointerEvent ) => {
        if( !isBoxSelecting || !boxStart || !boxEnd ) {
            setIsBoxSelecting( false );
            return;
        }
        const rect = containerRef.current!.getBoundingClientRect();
        const endScreen: [number, number] = [e.clientX - rect.left, e.clientY - rect.top];

        const [wx1, wy1] = screenToWorld( boxStart[0], boxStart[1] );
        const [wx2, wy2] = screenToWorld( endScreen[0], endScreen[1] );
        const minX = Math.min( wx1, wx2 );
        const maxX = Math.max( wx1, wx2 );
        const minY = Math.min( wy1, wy2 );
        const maxY = Math.max( wy1, wy2 );

        // determine which coordinate to test depending on visible layer
        const hits = pairs.filter( p => {
            if( hideB ) {
                // A rects are displayed with model matrix: screenX = ax*scaleX + tx, screenY = ay*scaleY + ty
                const sel = pairs.find( pp => pp.id === primarySelectedId );
                const { ax: sax, ay: say, bx: sbx, by: sby } = sel ?? { ax: 0, ay: 0, bx: offset[0], by: offset[1] };
                const ltx = sbx - sax * scaleX;
                const lty = sby - say * scaleY;
                const worldX = p.ax * scaleX + ltx;
                const worldY = p.ay * scaleY + lty;
                return worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY;
            } else {
                return p.bx >= minX && p.bx <= maxX && p.by >= minY && p.by <= maxY;
            }
        } );

        setSelectedIds( new Set( hits.map( h => h.id ) ) );
        setBoxStart( null );
        setBoxEnd( null );
        setIsBoxSelecting( false );
    }, [isBoxSelecting, boxStart, boxEnd, screenToWorld, pairs, hideB, primarySelectedId, offset, scaleX, scaleY] );

    // --- keyboard ---
    useEffect( () => {
        const handleKeyDown = ( e: KeyboardEvent ) => {
            // WASD: nudge only when exactly one pair is selected
            if( selectedIds.size === 1 && ["w", "a", "s", "d"].includes( e.key ) ) {
                e.preventDefault();
                setPairs( prev => prev.map( p => {
                    if( !selectedIds.has( p.id ) ) return p;
                    if( hideB ) {
                        switch( e.key ) {
                            case "w": return { ...p, ay: p.ay - NUDGE };
                            case "s": return { ...p, ay: p.ay + NUDGE };
                            case "a": return { ...p, ax: p.ax - NUDGE };
                            case "d": return { ...p, ax: p.ax + NUDGE };
                        }
                    } else {
                        switch( e.key ) {
                            case "w": return { ...p, by: p.by - NUDGE };
                            case "s": return { ...p, by: p.by + NUDGE };
                            case "a": return { ...p, bx: p.bx - NUDGE };
                            case "d": return { ...p, bx: p.bx + NUDGE };
                        }
                    }
                    return p;
                } ) );
                return;
            }

            switch( e.key ) {
                case "1": {
                    setBTransparent( true );
                    const sel = pairs.find( p => selectedIds.has( p.id ) );
                    // if( sel ) panToSelected( sel );
                    break;
                }
                case "2": {
                    setBTransparent( false );
                    const sel = pairs.find( p => selectedIds.has( p.id ) );
                    // if( sel ) panToSelected( sel );
                    break;
                }
                case "i": setOffset( ( [dx, dy] ) => [dx, dy - NUDGE] ); break;
                case "k": setOffset( ( [dx, dy] ) => [dx, dy + NUDGE] ); break;
                case "j": setOffset( ( [dx, dy] ) => [dx - NUDGE, dy] ); break;
                case "l": setOffset( ( [dx, dy] ) => [dx + NUDGE, dy] ); break;
                case "p": e.preventDefault(); setShowPairs( h => !h ); break;
                case "Tab": e.preventDefault(); setHideTiles( h => !h ); break;
                case "Escape": setSelectedIds( new Set() ); break;
                case "Delete":
                    if( selectedIds.size > 0 ) {
                        setPairs( prev => prev.filter( p => !selectedIds.has( p.id ) ) );
                        setSelectedIds( new Set() );
                    }
                    break;
                case "Enter":
                    if( selectedIds.size > 0 ) {
                        setPairs( prev => prev.map( p =>
                            selectedIds.has( p.id )
                                ? { ...p, status: p.status === RectStatus.Done ? RectStatus.New : RectStatus.Done }
                                : p
                        ) );
                    }
                    break;
            }
        };
        window.addEventListener( "keydown", handleKeyDown );
        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [selectedIds, hideB, pairs, panToSelected] );

    const onDeckClick = useCallback( ( info: any ) => {
        if( info?.object?.properties?.id != null ) {
            const id = info.object.properties.id;
            setSelectedIds( new Set( [id] ) );
            const sel = pairs.find( p => p.id === id );
            if( sel ) panToSelected( sel );
            return;
        }
        if( info && !info.object ) {
            const coord = info.coordinate;
            if( !coord ) return;
            const [bx, by] = coord;

            let ax: number, ay: number;
            if( pairs.length > 0 ) {
                let best = pairs[0];
                let bestDist = ( best.bx - bx ) ** 2 + ( best.by - by ) ** 2;
                for( let i = 1; i < pairs.length; i++ ) {
                    const d = ( pairs[i].bx - bx ) ** 2 + ( pairs[i].by - by ) ** 2;
                    if( d < bestDist ) { best = pairs[i]; bestDist = d; }
                }
                const dx = best.ax - best.bx;
                const dy = best.ay - best.by;
                ax = bx + dx;
                ay = by + dy;
            } else {
                ax = ( bx - offset[0] ) / scaleX;
                ay = ( by - offset[1] ) / scaleY;
            }

            const id = nextId;
            setPairs( prev => [...prev, { id, bx, by, ax, ay, score: null, matchCount: 0, status: RectStatus.New, src: "user" }] );
            setNextId( n => n + 1 );
            setSelectedIds( new Set( [id] ) );
        }
    }, [offset, scaleX, scaleY, nextId, pairs, hideB, panToSelected] );

    const handleDownloadCsv = useCallback( () => {
        const headers = ["ID", "B_X", "B_Y", "A_X", "A_Y", "DX", "DY", "SCORE"];
        const rows = pairs.map( p => [
            p.id,
            p.bx.toFixed( 2 ), p.by.toFixed( 2 ),
            p.ax.toFixed( 2 ), p.ay.toFixed( 2 ),
            ( p.ax - p.bx ).toFixed( 2 ), ( p.ay - p.by ).toFixed( 2 ),
            p.score ?? ""
        ] );
        downloadCsv( "pixel_mapping.csv", headers, rows );
    }, [pairs] );

    const handleImportCsv = useCallback( () => {
        const input = document.createElement( "input" );
        input.type = "file";
        input.accept = ".csv";
        input.onchange = () => {
            const file = input.files?.[0];
            if( !file ) return;
            const reader = new FileReader();
            reader.onload = () => {
                const text  = reader.result as string;
                const lines = text.trim().split( "\n" );
                if( lines.length < 2 ) return;
                const header     = lines[0].split( "," ).map( h => h.trim() );
                const idxId      = header.indexOf( "ID" );
                const idxBX      = header.indexOf( "B_X" );
                const idxBY      = header.indexOf( "B_Y" );
                const idxAX      = header.indexOf( "A_X" );
                const idxAY      = header.indexOf( "A_Y" );
                const idxMatches = header.indexOf( "N_MATCHES" );
                const idxConf    = header.indexOf( "CONFIDENCE" );
                const idxSrc     = header.indexOf( "SOURCE" );

                if( idxBX < 0 || idxBY < 0 || idxAX < 0 || idxAY < 0 ) return;
                const imported: RectPair[] = [];
                let maxId = pairs.reduce( ( m, p ) => Math.max( m, p.id ), 0 );

                for( let i = 1; i < lines.length; i++ ) {
                    const cols = lines[i].split( "," ).map( c => c.trim() );
                    if( cols.length < 4 ) continue;
                    const id   = idxId >= 0 ? parseInt( cols[idxId], 10 ) : ++maxId;
                    const bx   = parseFloat( cols[idxBX] );
                    const by   = parseFloat( cols[idxBY] );
                    const ax   = parseFloat( cols[idxAX] );
                    const ay   = parseFloat( cols[idxAY] );
                    const cnt  = parseFloat( cols[idxMatches] );
                    const conf = idxConf >= 0 ? parseFloat( cols[idxConf] ) : null;
                    const src  = cols[idxSrc] as PairSrc;
                    if( isNaN( bx ) || isNaN( by ) || isNaN( ax ) || isNaN( ay ) ) continue;
                    const score = conf !== null && !isNaN( conf ) ? conf : null;
                    imported.push( { id, bx, by, ax, ay, matchCount: cnt, score, status: RectStatus.New, src } );
                    if( id > maxId ) maxId = id;
                }
                setPairs( imported );
                setNextId( maxId + 1 );
                setSelectedIds( new Set() );
            };
            reader.readAsText( file );
        };
        input.click();
    }, [pairs] );

    interface PairAttrs {
        id:  number;
        img: "a" | "b";
        src: PairSrc;
        col: Color;
    }

    function getOffsetColor( rect: RectPair, MEAN_DX: number, MEAN_DY: number, MAX_DEV: number ): Color {
        const { ax, ay, bx, by } = rect;
        const devX = ( ax - bx ) - MEAN_DX;
        const devY = ( ay - by ) - MEAN_DY;
        const dist = Math.sqrt( devX * devX + devY * devY );
        const t = Math.min( dist / MAX_DEV, 1.0 );
        const r = Math.round( t * 255 );
        const a = Math.round( t * 128 );
        return [r, 0, 0, a];
    }

    function make( p: RectPair, img: "a" | "b", f: ( p: RectPair ) => number[] ) {
        const [x, y] = f( p );
        return {
            type: "Feature" as const,
            geometry: { type: "Polygon" as const, coordinates: [makeRectPolygon( x, y, RECT_SIZE / 2 )] },
            properties: {
                id:  p.id,
                src: p.src,
                img: img,
                col: getOffsetColor( p, MEAN_DX, MEAN_DY, MAX_DEV )
            }
        };
    }

    type FeatureCol = FeatureCollection<Geometry, PairAttrs>;
    const [aFeats, bFeats]: FeatureCol[] = useMemo( () => {
        const aFunc = ( p: RectPair ): [number, number] => [p.ax, p.ay];
        const bFunc = ( p: RectPair ): [number, number] => [p.bx, p.by];
        const rAs = pairs.map( x => make( x, "a", aFunc ) );
        const rBs = pairs.map( x => make( x, "b", bFunc ) );
        const aFeats: FeatureCol = { type: "FeatureCollection", features: rAs };
        const bFeats: FeatureCol = { type: "FeatureCollection", features: rBs };
        return [aFeats, bFeats];
    }, [pairs] );

    const selectedPair = useMemo( () => {
        return pairs.find( p => p.id === primarySelectedId );
    }, [pairs, primarySelectedId] );

    const { ax, ay, bx, by } = selectedPair ?? { ax: 0, ay: 0, bx: offset[0], by: offset[1] };
    const tx = bx - ax * scaleX;
    const ty = by - ay * scaleY;

    const getLineColor = ( { properties: rect }: Feature<Geometry, PairAttrs> ): Color => {
        if( selectedIds.has( rect.id ) ) return [0, 120, 255, 255]; // highlight selected
        return rect.src !== "rbf" ? selectedLineColor : filledLineColor;
    };

    const aRects = useMemo( () => new GeoJsonLayer( {
        id:       "a-rects",
        data:     aFeats,
        pickable: true,
        filled:   true,
        stroked:  true,
        opacity:  hideB && showPairs ? 1.0 : 0.0,
        getFillColor: ( { properties: rect }: Feature<Geometry, PairAttrs> ) => rect.col,
        getLineColor,
        getLineWidth: ( { properties: rect }: Feature<Geometry, PairAttrs> ) => selectedIds.has( rect.id ) ? 1 : 0.5,
        lineWidthMinPixels: 1,
        coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
        updateTriggers: { getLineColor: [selectedIds], getLineWidth: [selectedIds] },
        ...( hideB ? {
            modelMatrix: [
                scaleX, 0,      0, 0,
                0,      scaleY, 0, 0,
                0,      0,      1, 0,
                tx,     ty,     0, 1
            ]
        } : {} ),
    } ), [aFeats, selectedIds, hideB, offset, scaleX, scaleY, tx, ty, showPairs] );

    const bRects = useMemo( () => new GeoJsonLayer( {
        id:       "b-rects",
        data:     bFeats,
        pickable: true,
        filled:   true,
        stroked:  true,
        opacity:  !hideB && showPairs ? 1.0 : 0.0,
        getFillColor: ( { properties: rect }: Feature<Geometry, PairAttrs> ) => rect.col,
        getLineColor,
        getLineWidth: ( { properties: rect }: Feature<Geometry, PairAttrs> ) => selectedIds.has( rect.id ) ? 1 : 0.5,
        lineWidthMinPixels: 1,
        coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
        updateTriggers: { getLineColor: [selectedIds], getLineWidth: [selectedIds] },
    } ), [bFeats, selectedIds, hideB, offset, scaleX, scaleY, tx, ty, showPairs] );

    // --- tile layers ---
    const prevTileLayer = useMemo( () => new TileLayer<ImageBitmap>( {
        id: "aerial-tiles-a",
        visible: !hideTiles,
     // opacity: hideB ? inactiveOpacity : activeOpacity,
        pickable: false,
        tileSize: 512,
        refinementStrategy: "best-available",
        minZoom: -3,
        maxZoom:  0,
        extent: [0, 0, bWidth, bHeight],
        data: aerialImageTileSpec( imageA.aerialImageId ),
        modelMatrix: [
            scaleX, 0,      0, 0,
            0,      scaleY, 0, 0,
            0,      0,      1, 0,
            tx,     ty,     0, 1
        ],
        renderSubLayers: props => {
            const [[left, bottom], [right, top]] = props.tile.boundingBox;
            const { data, ...otherProps } = props;
            return new LanczosBitmapLayer( otherProps, {
                weight: 3,
                image: data,
                bounds: [left, top, right, bottom],
                textureParameters: { minFilter: "linear", magFilter: "linear" }
            } );
        }
    } ), [imageA, offset, bWidth, bHeight, scaleX, scaleY, hideTiles, selectedPair] );

    const tileLayer = useMemo( () => new TileLayer<ImageBitmap>( {
        id: "aerial-tiles-b",
        visible: !hideTiles,
        opacity: hideB ? inactiveOpacity : activeOpacity,
        pickable: false,
        tileSize: 512,
        refinementStrategy: "best-available",
        minZoom: -3,
        maxZoom:  0,
        extent: [0, 0, bWidth, bHeight],
        data: aerialImageTileSpec( imageB.aerialImageId ),
        renderSubLayers: props => {
            const [[left, bottom], [right, top]] = props.tile.boundingBox;
            const { data, ...otherProps } = props;
            return new LanczosBitmapLayer( otherProps, {
                weight: 3,
                image: data,
                transitions: { opacity: { duration: 150 } },
                bounds: [left, top, right, bottom],
                textureParameters: { minFilter: "nearest", magFilter: "nearest" }
            } );
        }
    } ), [hideB, imageB, bWidth, bHeight, hideTiles] );

    // --- selection box visual style ---
    const boxStyle = useMemo( (): React.CSSProperties | null => {
        if( !boxStart || !boxEnd ) return null;
        return {
            position: "absolute",
            left:   Math.min( boxStart[0], boxEnd[0] ),
            top:    Math.min( boxStart[1], boxEnd[1] ),
            width:  Math.abs( boxEnd[0] - boxStart[0] ),
            height: Math.abs( boxEnd[1] - boxStart[1] ),
            border: "2px dashed #4a9eff",
            backgroundColor: "rgba(74, 158, 255, 0.15)",
            zIndex: 11,
            pointerEvents: "none"
        };
    }, [boxStart, boxEnd] );

    return <div className="h-100">
        <div id="capture-list-page">
            <div id="capture-list">
                <div className="border border-top-0 border-start-0 border-end-0 d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                    <div className="flex-grow-1 ps-2 fs-5">
                        {hideB ? "Image A" : "Image B"} {JSON.stringify( offset )}
                        {selectedIds.size > 1 && <span className="ms-2 badge bg-primary">{selectedIds.size} selected</span>}
                    </div>
                    <Button onClick={handleImportCsv}>
                        <Upload />
                    </Button>
                    <Button onClick={handleDownloadCsv} disabled={pairs.length === 0}>
                        <Download />
                    </Button>
                </div>
                {( () => {
                    const visiblePairs = selectedIds.size > 0
                        ? pairs.filter( p => selectedIds.has( p.id ) )
                        : pairs;
                    return <List
                        className="gap-2"
                        rowComponent={PairRow}
                        rowCount={visiblePairs.length}
                        rowHeight={48}
                        rowProps={{ pairs: visiblePairs, selectedIds, onSelect: selectPair, onRemove: removePair }}
                    />;
                } )()}
            </div>
            <div
                className="image-viewer h-100 overflow-hidden position-relative"
                style={{ backgroundColor: "black" }}
                ref={containerRef}>

                {/* overlay to capture pointer during box selection */}
                {isBoxSelecting && (
                    <div
                        style={{ position: "absolute", inset: 0, zIndex: 10, cursor: "crosshair" }}
                        onPointerMove={handleBoxMove}
                        onPointerUp={handleBoxEnd}
                    />
                )}

                {/* selection rectangle visual */}
                {boxStyle && <div style={boxStyle} />}

                <div className="position-absolute d-flex flex-column gap-1" style={{ left: "0.75ex", top: "0.75ex", zIndex: 5, width: "max-content" }}>
                    {selectedIds.size === 1 && selectedPair && (
                        <div className="bg-body rounded shadow p-2 px-3 font-monospace">
                            <pre>
                                tx: {-tx.toFixed( 2 )};<br/>
                                cx: {MEAN_DX.toFixed( 2 )};<br/>
                                ty: {-ty.toFixed( 2 )};<br/>
                                cy: {MEAN_DY.toFixed( 2 )};<br/>
                                matches: {selectedPair.matchCount};<br/>
                                score: {selectedPair.score}
                            </pre>
                        </div>
                    )}
                    {selectedIds.size > 1 && ( () => {
                        const sel = pairs.filter( p => selectedIds.has( p.id ) );
                        const dxs = sel.map( p => p.ax - p.bx );
                        const dys = sel.map( p => p.ay - p.by );
                        const scores = sel.map( p => p.score ?? 0 );
                        const min = ( a: number[] ) => Math.min( ...a );
                        const max = ( a: number[] ) => Math.max( ...a );
                        const avg = ( a: number[] ) => a.reduce( ( s, v ) => s + v, 0 ) / a.length;
                        return (
                            <div className="bg-body rounded shadow p-2 px-3 font-monospace">
                                <pre>
                                    count: {sel.length}<br/>
                                    <br/>
                                    dx  min: {min( dxs ).toFixed( 2 )}<br/>
                                    dx  max: {max( dxs ).toFixed( 2 )}<br/>
                                    dx  avg: {avg( dxs ).toFixed( 2 )}<br/>
                                    <br/>
                                    dy  min: {min( dys ).toFixed( 2 )}<br/>
                                    dy  max: {max( dys ).toFixed( 2 )}<br/>
                                    dy  avg: {avg( dys ).toFixed( 2 )}<br/>
                                    <br/>
                                    score min: {( min( scores ) * 100 ).toFixed()}%<br/>
                                    score max: {( max( scores ) * 100 ).toFixed()}%<br/>
                                    score avg: {( avg( scores ) * 100 ).toFixed()}%
                                </pre>
                            </div>
                        );
                    } )()}
                </div>
                <DeckGL
                    views={new OrthographicView()}
                    layers={[prevTileLayer, tileLayer, aRects, bRects]}
                    viewState={viewState}
                    onViewStateChange={( { viewState: vs } ) => setViewState( vs as OrthographicViewState )}
                    controller={!isBoxSelecting}
                    onClick={onDeckClick}
                    style={{ backgroundColor: "black" }}
                    getCursor={( { isDragging, isHovering } ) => {
                        if( isDragging ) return "grabbing";
                        if( isHovering ) return "pointer";
                        return "grab";
                    }}
                />
            </div>
        </div>
    </div>;
}